import React, { useState, useEffect } from "react";
import { ArrowRight } from "lucide-react";

const HeroSection = () => {
  const [nodes, setNodes] = useState<Array<{ x: number; y: number; id: number }>>([]);

  useEffect(() => {
    const nodeCount = 20;
    const newNodes = Array.from({ length: nodeCount }, (_, i) => ({
      x: Math.random() * 100,
      y: Math.random() * 100,
      id: i,
    }));
    setNodes(newNodes);
  }, []);

  return (
    <section className="relative py-20 mb-20">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <svg className="w-full h-full absolute inset-0" xmlns="http://www.w3.org/2000/svg">
          {nodes.map((node, i) =>
            nodes.slice(i + 1).map((otherNode, j) => {
              const distance = Math.sqrt(
                Math.pow(node.x - otherNode.x, 2) + Math.pow(node.y - otherNode.y, 2)
              );
              if (distance < 25) {
                return (
                  <line
                    key={`${i}-${j}`}
                    x1={`${node.x}%`}
                    y1={`${node.y}%`}
                    x2={`${otherNode.x}%`}
                    y2={`${otherNode.y}%`}
                    stroke="#cb6ce6"
                    strokeWidth="1"
                    opacity={0.2 - distance / 125}
                    style={{
                      animation: `pulse ${3 + Math.random() * 2}s ease-in-out infinite`,
                      animationDelay: `${Math.random() * 2}s`,
                    }}
                  />
                );
              }
              return null;
            })
          )}
          {nodes.map((node) => (
            <circle
              key={node.id}
              cx={`${node.x}%`}
              cy={`${node.y}%`}
              r="3"
              fill="#cb6ce6"
              opacity="0.6"
              style={{
                animation: `pulse ${2 + Math.random()}s ease-in-out infinite`,
                animationDelay: `${Math.random() * 2}s`,
              }}
            />
          ))}
        </svg>
      </div>

      <div className="text-center relative z-10">
        <div className="flex justify-center mb-8">
          <div
            className="p-6 rounded-3xl relative overflow-hidden flex items-center justify-center"
            style={{
              background: `
                linear-gradient(135deg, #cb6ce6 0%, #a855f7 50%, #9333ea 100%),
                radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.3) 0%, transparent 50%)
              `,
              boxShadow: `
                0 15px 40px rgba(203, 108, 230, 0.5),
                inset 0 1px 0 rgba(255, 255, 255, 0.3),
                0 0 0 1px rgba(203, 108, 230, 0.4)
              `,
              animation: "float 6s ease-in-out infinite",
              width: '100px',
              height: '100px',
            }}
          >
            <span
              className="text-white"
              style={{
                fontSize: '64px',
                fontWeight: '700',
                fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
              }}
            >
              Ø
            </span>
            <div
              className="absolute inset-0 opacity-50"
              style={{
                background: "radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.3) 0%, transparent 70%)",
                filter: "blur(15px)",
              }}
            />
          </div>
        </div>

        <h1
          className="text-7xl font-bold mb-6 relative inline-block"
          style={{
            background: "linear-gradient(135deg, #ffffff 0%, #e2e8f0 50%, #cbd5e1 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            fontWeight: "700",
            letterSpacing: "-0.03em",
            textShadow: "0 4px 12px rgba(0, 0, 0, 0.5)",
          }}
        >
          Øfun
        </h1>

        <p
          className="text-2xl mb-12 max-w-4xl mx-auto"
          style={{
            color: "#e2e8f0",
            fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            fontWeight: "400",
            letterSpacing: "0.01em",
            lineHeight: "1.7",
          }}
        >
          Øfun is a modular blockchain built for{" "}
          <span
            style={{
              background: "linear-gradient(135deg, #cb6ce6 0%, #e879f9 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              fontWeight: "600",
            }}
          >
            fairness, community, and fun
          </span>
          {" "}— powered by{" "}
          <span
            style={{
              background: "linear-gradient(135deg, #cb6ce6 0%, #e879f9 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              fontWeight: "600",
            }}
          >
            zk-proofs, Celestia DA
          </span>
          , and{" "}
          <span
            style={{
              background: "linear-gradient(135deg, #cb6ce6 0%, #e879f9 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              fontWeight: "600",
            }}
          >
            light-client-first design
          </span>
          .
        </p>

        <div className="flex gap-6 justify-center">
          <a
            href="https://github.com/zerofunxyz/Ofun"
            target="_blank"
            rel="noopener noreferrer"
            className="group px-8 py-4 rounded-xl transition-all duration-300 relative overflow-hidden flex items-center gap-3"
            style={{
              background: "linear-gradient(135deg, #cb6ce6 0%, #a855f7 100%)",
              boxShadow: `
                0 8px 25px rgba(203, 108, 230, 0.4),
                inset 0 1px 0 rgba(255, 255, 255, 0.2)
              `,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px) scale(1.02)";
              e.currentTarget.style.boxShadow = `
                0 15px 35px rgba(203, 108, 230, 0.6),
                inset 0 1px 0 rgba(255, 255, 255, 0.3),
                0 0 40px rgba(203, 108, 230, 0.4)
              `;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0px) scale(1)";
              e.currentTarget.style.boxShadow = `
                0 8px 25px rgba(203, 108, 230, 0.4),
                inset 0 1px 0 rgba(255, 255, 255, 0.2)
              `;
            }}
          >
            <span
              className="text-white font-medium text-lg relative z-10"
              style={{
                fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
              }}
            >
              Explore GitHub
            </span>
            <ArrowRight className="w-5 h-5 text-white group-hover:translate-x-1 transition-transform duration-300" />
          </a>

          <a
            href="https://x.com/zerofunxyz"
            target="_blank"
            rel="noopener noreferrer"
            className="px-8 py-4 rounded-xl transition-all duration-300 relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, rgba(51, 65, 85, 0.6) 0%, rgba(30, 41, 59, 0.8) 100%)",
              boxShadow: `
                0 4px 15px rgba(0, 0, 0, 0.2),
                inset 0 1px 0 rgba(255, 255, 255, 0.1),
                0 0 0 1px rgba(203, 108, 230, 0.3)
              `,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = `
                0 8px 25px rgba(0, 0, 0, 0.3),
                inset 0 1px 0 rgba(255, 255, 255, 0.2),
                0 0 0 1px rgba(203, 108, 230, 0.5),
                0 0 30px rgba(203, 108, 230, 0.3)
              `;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0px)";
              e.currentTarget.style.boxShadow = `
                0 4px 15px rgba(0, 0, 0, 0.2),
                inset 0 1px 0 rgba(255, 255, 255, 0.1),
                0 0 0 1px rgba(203, 108, 230, 0.3)
              `;
            }}
          >
            <span
              className="font-medium text-lg"
              style={{
                color: "#e2e8f0",
                fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
              }}
            >
              Follow on X
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}

export default HeroSection;
